import type { ThreadAsset, ThreadCluster, ThreadColumn, ThreadHealth, ThreadRow, ThreadSelectionState } from "../thread-design-system";
import type { GraphResponse, ScenarioResponse, TableRowRecord } from "../../pages/v2-workbench/types";
import type {
  V3ProjectionCell,
  V3ProjectionRow,
  V3ProvenanceCluster,
  V3ProvenanceEdge,
  V3ProvenanceGraph,
  V3ProvenanceNode,
  V3ProvenanceNodeType,
} from "./types";

const PROJECTION_ID = "projection:thread-v3:active-table";
const LIVE_GRAPH_NODE_BUDGET = 700;
const LIVE_GRAPH_EDGE_BUDGET = 1_400;
const PROJECTION_ROW_BUDGET = 1_200;
const SCENARIO_LINEAGE_ROW_BUDGET = 1_200;
const SCENARIO_PLOT_POINT_BUDGET = 1_500;

const CLUSTER_LAYOUT: Record<string, { label: string; type: string; x: number; y: number; r: number }> = {
  source: { label: "Sources", type: "source", x: 80, y: 320, r: 56 },
  condition: { label: "Run Parameters", type: "condition", x: 210, y: 135, r: 54 },
  geometry: { label: "Geometry", type: "geometry", x: 235, y: 492, r: 54 },
  mesh: { label: "Meshes", type: "mesh", x: 405, y: 460, r: 60 },
  model: { label: "Models", type: "model", x: 405, y: 140, r: 54 },
  run: { label: "Solver Runs", type: "run", x: 600, y: 310, r: 72 },
  quantity: { label: "Quantities", type: "quantity", x: 805, y: 310, r: 70 },
  plot: { label: "Plots", type: "plot", x: 1005, y: 150, r: 54 },
  presentation: { label: "Decks / Claims", type: "presentation", x: 1170, y: 310, r: 60 },
  artifact: { label: "Artifacts", type: "artifact", x: 1045, y: 510, r: 58 },
  operation: { label: "Operations", type: "operation", x: 620, y: 530, r: 56 },
};

function stableHash(text: string): number {
  let hash = 2166136261;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function safeId(value: unknown, fallback: string): string {
  const raw = String(value ?? fallback).trim() || fallback;
  return raw.replace(/[^a-zA-Z0-9_.:-]+/g, "-");
}

function positionInCluster(cluster: string, id: string): { x: number; y: number } {
  const layout = CLUSTER_LAYOUT[cluster] ?? CLUSTER_LAYOUT.artifact!;
  const h = stableHash(`${cluster}:${id}`);
  const angle = ((h % 36000) / 36000) * Math.PI * 2;
  const radius = Math.sqrt(((h >>> 8) % 10000) / 10000) * layout.r * 0.78;
  return {
    x: Number((layout.x + Math.cos(angle) * radius).toFixed(2)),
    y: Number((layout.y + Math.sin(angle) * radius * 0.72).toFixed(2)),
  };
}

function clusterForAsset(asset: ThreadAsset): string {
  if (asset.type === "plot" || asset.type === "correlogram-view") return "plot";
  if (asset.type === "deck" || asset.type === "slide" || asset.type === "slide-deck" || asset.type === "storyboard" || asset.type === "runway-video") return "presentation";
  return "artifact";
}

function typeForAsset(asset: ThreadAsset): V3ProvenanceNodeType {
  if (asset.type === "plot" || asset.type === "correlogram-view") return "plot";
  if (asset.type === "deck" || asset.type === "slide-deck") return "deck";
  if (asset.type === "spreadsheet") return "spreadsheet";
  if (asset.type === "chat") return "chat_thread";
  if (asset.type === "email") return "email_thread";
  if (asset.type === "uvf") return "uvf_view";
  if (asset.type === "notebook") return "notebook";
  if (asset.type === "decision") return "decision_summary";
  if (asset.type === "storyboard" || asset.type === "runway-video") return "storyboard";
  if (asset.type === "report") return "report";
  return "artifact";
}

function sourceNodeForColumn(row: ThreadRow, column: ThreadColumn): { id: string; type: V3ProvenanceNodeType; label: string; cluster: string; attrs?: Record<string, unknown> } {
  const runKey = safeId(row.runId, "run");
  if (column.kind === "condition" || ["alpha", "mach", "re", "reynolds", "conditionKey"].includes(column.id)) {
    return {
      id: `run:${runKey}`,
      type: "solver_run",
      label: `Run parameters · ${row.runId}`,
      cluster: "run",
      attrs: { runId: row.runId, alpha: row.alpha, mach: row.mach, reynolds: row.re, conditionKey: row.conditionKey },
    };
  }
  if (column.kind === "family" || column.id === "family" || column.id === "modelFamily" || column.id === "source_tool") {
    return { id: `model:${safeId(row.family, "model")}`, type: "model_family", label: `Model · ${row.family}`, cluster: "model" };
  }
  if (column.id === "mesh") return { id: `mesh:${safeId(row.mesh, runKey)}`, type: "mesh", label: `Mesh · ${row.mesh || row.runId}`, cluster: "mesh" };
  if (column.id === "design" || column.id === "geometry") return { id: `geometry:${safeId(row.design, runKey)}`, type: "geometry", label: `Geometry · ${row.design || row.runId}`, cluster: "geometry" };
  if (column.id === "script" || column.id === "operation" || column.id === "source_id") {
    return { id: `operation:${safeId(row.script, runKey)}`, type: "operation", label: `Operation · ${row.script || row.runId}`, cluster: "operation" };
  }
  if (column.id === "correction") return { id: `correction:${safeId(row.correction, runKey)}`, type: "correction", label: `Correction · ${row.correction || row.runId}`, cluster: "model" };
  if (column.id === "status" || column.id === "reviewState") return { id: `review:${runKey}`, type: "review_state", label: `Review · ${row.status ?? "unknown"}`, cluster: "run" };
  if (column.kind === "quantity") {
    return {
      id: `quantity:${runKey}:${safeId(column.id, "quantity")}`,
      type: "quantity_value",
      label: `${column.label} · ${row.runId}`,
      cluster: "quantity",
      attrs: { runId: row.runId, quantity: column.id, value: row[column.id] },
    };
  }
  return { id: `run:${runKey}`, type: "solver_run", label: `Run · ${row.runId}`, cluster: "run", attrs: { runId: row.runId } };
}

function buildMaps(edges: V3ProvenanceEdge[]): Pick<V3ProvenanceGraph, "incomingByNode" | "outgoingByNode"> {
  const incomingByNode = new Map<string, V3ProvenanceEdge[]>();
  const outgoingByNode = new Map<string, V3ProvenanceEdge[]>();
  for (const edge of edges) {
    if (!outgoingByNode.has(edge.from)) outgoingByNode.set(edge.from, []);
    if (!incomingByNode.has(edge.to)) incomingByNode.set(edge.to, []);
    outgoingByNode.get(edge.from)!.push(edge);
    incomingByNode.get(edge.to)!.push(edge);
  }
  return { incomingByNode, outgoingByNode };
}

function walk(
  seedIds: readonly string[],
  adjacency: Map<string, V3ProvenanceEdge[]>,
  nextIdForEdge: (edge: V3ProvenanceEdge) => string,
  maxDepth = 5,
  maxNodes = 450,
): Set<string> {
  const visited = new Set<string>();
  const queue = seedIds.map((id) => ({ id, depth: 0 }));
  while (queue.length && visited.size < maxNodes) {
    const current = queue.shift();
    if (!current || visited.has(current.id) || current.depth > maxDepth) continue;
    visited.add(current.id);
    for (const edge of adjacency.get(current.id) ?? []) {
      const nextId = nextIdForEdge(edge);
      if (!visited.has(nextId)) queue.push({ id: nextId, depth: current.depth + 1 });
    }
  }
  return visited;
}

export function walkProvenanceUpstream(graph: V3ProvenanceGraph, seedIds: readonly string[], maxDepth = 5, maxNodes = 450): Set<string> {
  return walk(seedIds, graph.incomingByNode, (edge) => edge.from, maxDepth, maxNodes);
}

export function walkProvenanceDownstream(graph: V3ProvenanceGraph, seedIds: readonly string[], maxDepth = 5, maxNodes = 450): Set<string> {
  return walk(seedIds, graph.outgoingByNode, (edge) => edge.to, maxDepth, maxNodes);
}

export function deriveRowsFromProvenanceGraph(graph: V3ProvenanceGraph, rows: readonly ThreadRow[]): ThreadRow[] {
  const projectionsByRunId = new Map(graph.rows.map((row) => [row.runId, row]));
  return rows.map((row) => {
    const projectionRow = projectionsByRunId.get(row.runId);
    if (!projectionRow) return row;
    return {
      ...row,
      rowNodeId: projectionRow.handleId,
      cellNodeIds: projectionRow.cellHandleIds,
    };
  });
}

export function deriveProvenanceSelection(
  graph: V3ProvenanceGraph,
  assets: readonly ThreadAsset[],
  selection: ThreadSelectionState,
): {
  selectedNodeId: string | null;
  upstream: Set<string>;
  downstream: Set<string>;
  litNodeIds: Set<string>;
  litAssetIds: Set<string>;
  litRowIds: Set<string>;
  litClusterIds: Set<string>;
} {
  const primary = selection.primary;
  const cellProjection = primary.kind === "cell" ? graph.cellsByRowCol.get(`${primary.rowId}:${primary.colId}`) : null;
  const rowProjection = primary.kind === "row" ? graph.rows.find((row) => row.runId === primary.rowId) : null;
  const cellSourceNodeId = cellProjection?.sourceNodeIds[0] ?? null;
  const rowSourceNodeId =
    rowProjection?.sourceNodeIds.find((nodeId) => graph.nodesById.get(nodeId)?.type === "solver_run") ??
    rowProjection?.sourceNodeIds[0] ??
    null;
  const selectedNodeId =
    primary.kind === "cell" ? cellSourceNodeId :
    primary.kind === "row" ? rowSourceNodeId :
    primary.kind === "asset" ? primary.nodeId ?? graph.assetNodeIdByAssetId.get(primary.assetId) ?? null :
    primary.kind === "dag-node" ? primary.nodeId :
    null;

  if (!selectedNodeId) {
    return {
      selectedNodeId: null,
      upstream: new Set(),
      downstream: new Set(),
      litNodeIds: new Set(),
      litAssetIds: new Set(),
      litRowIds: new Set(),
      litClusterIds: new Set(),
    };
  }

  const upstream = walkProvenanceUpstream(graph, [selectedNodeId], primary.kind === "asset" ? 7 : 5, primary.kind === "asset" ? 700 : 450);
  const downstream = primary.kind === "asset" ? new Set<string>() : walkProvenanceDownstream(graph, [selectedNodeId], 6, 650);
  const litNodeIds = new Set([selectedNodeId, ...upstream, ...downstream]);
  const litAssetIds = new Set<string>();
  const litRowIds = new Set<string>();
  const litClusterIds = new Set<string>();

  if (primary.kind === "cell" || primary.kind === "row") litRowIds.add(primary.rowId);

  for (const id of litNodeIds) {
    const node = graph.nodesById.get(id);
    if (node?.cluster) litClusterIds.add(node.cluster);
  }

  for (const row of graph.rows) {
    if (row.sourceNodeIds.some((nodeId) => litNodeIds.has(nodeId))) litRowIds.add(row.runId);
    if (row.cells.some((cell) => cell.sourceNodeIds.some((nodeId) => litNodeIds.has(nodeId)))) litRowIds.add(row.runId);
  }

  for (const asset of assets) {
    const assetNodeId = asset.provenanceNodeId ?? graph.assetNodeIdByAssetId.get(asset.id);
    if (assetNodeId && litNodeIds.has(assetNodeId)) litAssetIds.add(asset.id);
    if (primary.kind === "cell" && asset.refs.cells?.some((cell) => cell.runId === primary.rowId && cell.col === primary.colId)) litAssetIds.add(asset.id);
    if ((primary.kind === "cell" || primary.kind === "row") && asset.refs.rows?.includes(primary.rowId)) litAssetIds.add(asset.id);
  }

  return { selectedNodeId, upstream, downstream, litNodeIds, litAssetIds, litRowIds, litClusterIds };
}

export function buildV3ProvenanceGraph(input: {
  rows: readonly ThreadRow[];
  totalRowCount?: number;
  columns: readonly ThreadColumn[];
  assets: readonly ThreadAsset[];
  clusters: readonly ThreadCluster[];
  scenario: ScenarioResponse | null;
  graph: GraphResponse | null;
}): V3ProvenanceGraph {
  const nodes = new Map<string, V3ProvenanceNode>();
  const edges = new Map<string, V3ProvenanceEdge>();
  const projectionRows: V3ProjectionRow[] = [];
  const cellsByRowCol = new Map<string, V3ProjectionCell>();
  const assetNodeIdByAssetId = new Map<string, string>();

  function addNode(node: Omit<V3ProvenanceNode, "position"> & { position?: { x: number; y: number } }): V3ProvenanceNode {
    const existing = nodes.get(node.id);
    if (existing) return existing;
    const next: V3ProvenanceNode = {
      ...node,
      position: node.position ?? positionInCluster(node.cluster, node.id),
    };
    nodes.set(next.id, next);
    return next;
  }

  function addEdge(from: string | undefined, to: string | undefined, type: string, attrs?: Record<string, unknown>) {
    if (!from || !to || from === to) return;
    const id = `edge:${safeId(from, "from")}:${safeId(to, "to")}:${type}`;
    if (!edges.has(id)) edges.set(id, { id, from, to, type, attrs });
  }

  addNode({ id: "source:thread-live-record", type: "source_dataset", label: "Thread live record", cluster: "source" });

  const liveGraphNodeIds = new Set<string>();
  for (const liveNode of (input.graph?.nodes ?? []).slice(0, LIVE_GRAPH_NODE_BUDGET)) {
    const type = liveNode.node_type === "operation" ? "operation" : typeForLiveGraphNode(liveNode.kind);
    const cluster = liveNode.node_type === "operation" ? "operation" : clusterForLiveGraphNode(liveNode.kind);
    liveGraphNodeIds.add(liveNode.id);
    addNode({
      id: liveNode.id,
      type,
      label: liveNode.label,
      cluster,
      attrs: {
        kind: liveNode.kind,
        nodeType: liveNode.node_type,
        payloadPreview: liveNode.payload_preview,
        metadataPreview: liveNode.metadata_preview,
        configPreview: liveNode.config_preview,
      },
      health: liveNode.status,
    });
  }
  let liveEdgeCount = 0;
  for (const liveEdge of input.graph?.edges ?? []) {
    if (liveEdgeCount >= LIVE_GRAPH_EDGE_BUDGET) break;
    if (!liveGraphNodeIds.has(liveEdge.source) || !liveGraphNodeIds.has(liveEdge.target)) continue;
    addEdge(liveEdge.source, liveEdge.target, liveEdge.role || liveEdge.direction);
    liveEdgeCount += 1;
  }

  const projectionInputRows = input.rows.slice(0, PROJECTION_ROW_BUDGET);
  for (let rowIndex = 0; rowIndex < projectionInputRows.length; rowIndex += 1) {
    const row = projectionInputRows[rowIndex]!;
    const runKey = safeId(row.runId, "run");
    const source = addNode({ id: "source:thread-live-record", type: "source_dataset", label: "Thread live record", cluster: "source" });
    const model = addNode({ id: `model:${safeId(row.family, "model")}`, type: "model_family", label: `Model · ${row.family}`, cluster: "model", attrs: { family: row.family } });
    const geometry = addNode({ id: `geometry:${safeId(row.design, runKey)}`, type: "geometry", label: `Geometry · ${row.design || row.runId}`, cluster: "geometry" });
    const mesh = addNode({ id: `mesh:${safeId(row.mesh, runKey)}`, type: "mesh", label: `Mesh · ${row.mesh || row.runId}`, cluster: "mesh" });
    const run = addNode({
      id: `run:${runKey}`,
      type: "solver_run",
      label: `Run · ${row.runId}`,
      cluster: "run",
      attrs: { runId: row.runId, family: row.family, alpha: row.alpha, mach: row.mach, reynolds: row.re, conditionKey: row.conditionKey },
      health: row.status,
    });
    const operation = addNode({ id: `operation:${safeId(row.script, runKey)}`, type: "operation", label: `Operation · ${row.script || row.runId}`, cluster: "operation" });
    const review = addNode({ id: `review:${runKey}`, type: "review_state", label: `Review · ${row.status ?? "unknown"}`, cluster: "run", attrs: { state: row.status } });
    const rowHandleId = `row:${PROJECTION_ID}:${runKey}`;
    const cellHandleIds: Record<string, string> = {};
    const projectionCells: V3ProjectionCell[] = [];

    addEdge(source.id, run.id, "defines_run_parameters", { alpha: row.alpha, mach: row.mach, reynolds: row.re, conditionKey: row.conditionKey });
    addEdge(source.id, geometry.id, "defines_geometry");
    addEdge(geometry.id, mesh.id, "meshed_as");
    addEdge(mesh.id, run.id, "used_by");
    addEdge(model.id, run.id, "solver_family");
    addEdge(operation.id, run.id, "executed");
    addEdge(run.id, review.id, "reviewed_as");

    for (let columnIndex = 0; columnIndex < input.columns.length; columnIndex += 1) {
      const column = input.columns[columnIndex]!;
      const sourceSpec = sourceNodeForColumn(row, column);
      const sourceNode = addNode(sourceSpec);
      if (sourceNode.type === "quantity_value") {
        addEdge(run.id, sourceNode.id, "produced");
        addEdge(operation.id, sourceNode.id, "extracted_by");
        const correction = addNode({ id: `correction:${safeId(String(row.correction ?? row.script ?? row.family), runKey)}`, type: "correction", label: `Correction · ${row.correction ?? row.script ?? row.family}`, cluster: "model" });
        addEdge(correction.id, sourceNode.id, "corrected_by");
      }
      const cellHandleId = `cell:${PROJECTION_ID}:${runKey}:${safeId(column.id, "col")}`;
      cellHandleIds[column.id] = cellHandleId;
      const projectionCell = {
        handleId: cellHandleId,
        rowHandleId,
        rowId: row.runId,
        colId: column.id,
        sourceNodeIds: [sourceNode.id],
        value: row[column.id],
      };
      cellsByRowCol.set(`${row.runId}:${column.id}`, projectionCell);
      projectionCells.push(projectionCell);
    }

    projectionRows.push({
      handleId: rowHandleId,
      runId: row.runId,
      sourceNodeIds: [source.id, model.id, geometry.id, mesh.id, run.id, operation.id, review.id],
      cellHandleIds,
      cells: projectionCells,
    });
  }

  for (const asset of input.assets) {
    const assetNodeId = asset.provenanceNodeId ?? `asset:${safeId(asset.id, "asset")}`;
    assetNodeIdByAssetId.set(asset.id, assetNodeId);
    const assetNode = addNode({
      id: assetNodeId,
      type: typeForAsset(asset),
      label: asset.label,
      cluster: clusterForAsset(asset),
      attrs: { assetId: asset.id, source: asset.source, type: asset.type },
      health: asset.health,
    });
    connectAssetChildren(asset, assetNode.id, addNode, addEdge);
    for (const ref of (asset.refs.cells ?? []).slice(0, 80)) {
      const projectionCell = cellsByRowCol.get(`${ref.runId}:${ref.col}`);
      for (const sourceNodeId of projectionCell?.sourceNodeIds ?? []) {
        addEdge(sourceNodeId, assetNode.id, "source_consumed_by");
      }
    }
    for (const rowId of (asset.refs.rows ?? []).slice(0, 80)) {
      const projectionRow = projectionRows.find((row) => row.runId === rowId);
      for (const sourceNodeId of projectionRow?.sourceNodeIds ?? []) {
        addEdge(sourceNodeId, assetNode.id, "row_slice_consumed_by");
      }
      const preferredCell = cellsByRowCol.get(`${rowId}:cl`) ?? cellsByRowCol.get(`${rowId}:C_L`) ?? cellsByRowCol.get(`${rowId}:cd`);
      for (const sourceNodeId of preferredCell?.sourceNodeIds ?? []) {
        addEdge(sourceNodeId, assetNode.id, "source_consumed_by");
      }
    }
  }

  connectScenarioLineage(input.scenario, input.columns, cellsByRowCol, addNode, addEdge);

  const nodeList = [...nodes.values()];
  const edgeList = [...edges.values()];
  const clusterCounts = new Map<string, number>();
  for (const node of nodeList) clusterCounts.set(node.cluster, (clusterCounts.get(node.cluster) ?? 0) + 1);
  for (const cluster of input.clusters) {
    const id = normalizeCluster(cluster.id || cluster.type);
    if (!CLUSTER_LAYOUT[id]) {
      CLUSTER_LAYOUT[id] = {
        label: cluster.label,
        type: cluster.type,
        x: 150 + (stableHash(id) % 900),
        y: 140 + (stableHash(`${id}:y`) % 360),
        r: 70,
      };
    }
    clusterCounts.set(id, Math.max(clusterCounts.get(id) ?? 0, cluster.nodes));
  }
  const clusters = [...clusterCounts.entries()].map(([id, totalCount]) => {
    const layout = CLUSTER_LAYOUT[id] ?? CLUSTER_LAYOUT.artifact!;
    return {
      id,
      label: layout.label,
      type: layout.type,
      position: { x: layout.x, y: layout.y },
      radius: layout.r,
      totalCount,
    } satisfies V3ProvenanceCluster;
  });

  const nodesById = new Map(nodeList.map((node) => [node.id, node]));
  const maps = buildMaps(edgeList);
  const fullRowCount = input.totalRowCount ?? input.rows.length;
  const estimatedProjectionNodes = fullRowCount * (input.columns.length + 7) + input.assets.length * 5;
  const estimatedProjectionEdges = fullRowCount * (input.columns.length + 9) + input.assets.length * 12;
  return {
    projectionId: PROJECTION_ID,
    nodes: nodeList,
    edges: edgeList,
    clusters,
    rows: projectionRows,
    nodesById,
    ...maps,
    cellsByRowCol,
    assetNodeIdByAssetId,
    representedScale: {
      totalNodes: Math.max(nodeList.length, input.graph?.nodes.length ?? 0, estimatedProjectionNodes),
      totalEdges: Math.max(edgeList.length, input.graph?.edges.length ?? 0, estimatedProjectionEdges),
    },
    source: "live-synthesized",
  };
}

function typeForLiveGraphNode(kind: string): V3ProvenanceNodeType {
  const value = kind.toLowerCase();
  if (value.includes("plot")) return "plot";
  if (value.includes("deck") || value.includes("slide")) return "deck";
  if (value.includes("sheet") || value.includes("workbook")) return "spreadsheet";
  if (value.includes("table")) return "artifact";
  if (value.includes("report")) return "report";
  return "artifact";
}

function clusterForLiveGraphNode(kind: string): string {
  const value = kind.toLowerCase();
  if (value.includes("plot")) return "plot";
  if (value.includes("deck") || value.includes("slide")) return "presentation";
  if (value.includes("table")) return "artifact";
  return "artifact";
}

function normalizeCluster(value: string): string {
  const lower = value.toLowerCase();
  if (lower.includes("plot")) return "plot";
  if (lower.includes("deck") || lower.includes("slide") || lower.includes("presentation")) return "presentation";
  if (lower.includes("table")) return "artifact";
  if (lower.includes("run")) return "run";
  if (lower.includes("operation")) return "operation";
  if (lower.includes("mesh")) return "mesh";
  if (lower.includes("source")) return "source";
  return safeId(lower, "artifact");
}

function connectAssetChildren(
  asset: ThreadAsset,
  assetNodeId: string,
  addNode: (node: Omit<V3ProvenanceNode, "position"> & { position?: { x: number; y: number } }) => V3ProvenanceNode,
  addEdge: (from: string | undefined, to: string | undefined, type: string, attrs?: Record<string, unknown>) => void,
) {
  if (asset.type === "plot") {
    const trace = addNode({ id: `${assetNodeId}:trace:primary`, type: "plot_trace", label: `${asset.label} · trace`, cluster: "plot" });
    addEdge(trace.id, assetNodeId, "contains");
    for (const cell of asset.refs.cells?.slice(0, 24) ?? []) {
      const point = addNode({ id: `${assetNodeId}:point:${safeId(cell.runId, "row")}:${safeId(cell.col, "col")}`, type: "plot_point", label: `${cell.runId} · ${cell.col}`, cluster: "plot" });
      addEdge(point.id, trace.id, "contains");
    }
  } else if (asset.type === "deck" || asset.type === "slide-deck") {
    const slide = addNode({ id: `${assetNodeId}:slide:summary`, type: "slide", label: `${asset.label} · slide`, cluster: "presentation", health: asset.health });
    const claim = addNode({ id: `${assetNodeId}:claim:primary`, type: "claim", label: `${asset.label} · claim`, cluster: "presentation", health: asset.health });
    addEdge(claim.id, slide.id, "supports");
    addEdge(slide.id, assetNodeId, "contained_by");
  } else if (asset.type === "spreadsheet") {
    const range = addNode({ id: `${assetNodeId}:range:primary`, type: "spreadsheet_range", label: `${asset.label} · range`, cluster: "artifact", health: asset.health });
    addEdge(range.id, assetNodeId, "contained_by");
  } else if (asset.type === "chat" || asset.type === "email") {
    const summary = addNode({ id: `${assetNodeId}:summary`, type: "smart_summary", label: `${asset.label} · smart summary`, cluster: "artifact", health: asset.health });
    addEdge(summary.id, assetNodeId, "summarizes");
  } else if (asset.type === "storyboard" || asset.type === "runway-video") {
    for (let index = 1; index <= 3; index += 1) {
      const shot = addNode({ id: `${assetNodeId}:shot:${index}`, type: "storyboard_shot", label: `${asset.label} · shot ${index}`, cluster: "presentation", health: asset.health });
      addEdge(shot.id, assetNodeId, "contained_by");
    }
  }
}

function connectScenarioLineage(
  scenario: ScenarioResponse | null,
  columns: readonly ThreadColumn[],
  cellsByRowCol: Map<string, V3ProjectionCell>,
  addNode: (node: Omit<V3ProvenanceNode, "position"> & { position?: { x: number; y: number } }) => V3ProvenanceNode,
  addEdge: (from: string | undefined, to: string | undefined, type: string, attrs?: Record<string, unknown>) => void,
) {
  for (const table of scenario?.tables ?? []) {
    for (const row of table.rows.slice(0, SCENARIO_LINEAGE_ROW_BUDGET)) {
      const rowId = rowIdFromScenario(row, table.row_id_field);
      for (const [field, lineage] of Object.entries(row._cell_lineage ?? {})) {
        const colId = columns.find((column) => column.id === field || column.label === field)?.id ?? field;
        const cell = cellsByRowCol.get(`${rowId}:${colId}`);
        if (lineage?.artifact_id) {
          for (const sourceNodeId of cell?.sourceNodeIds ?? []) {
            addEdge(sourceNodeId, lineage.artifact_id, lineage.role || "lineage");
          }
        }
      }
    }
  }
  for (const plot of scenario?.plots ?? []) {
    const plotNode = addNode({ id: plot.artifact_id, type: "plot", label: plot.label, cluster: "plot" });
    let pointCount = 0;
    for (const point of plot.points) {
      if (pointCount >= SCENARIO_PLOT_POINT_BUDGET) break;
      const rowId = point.flat_view_row_id ?? point.joined_row_id;
      if (!rowId) continue;
      const metric = typeof point.metric === "string" ? point.metric.toLowerCase() : "cl";
      const cell = cellsByRowCol.get(`${rowId}:${metric}`) ?? cellsByRowCol.get(`${rowId}:cl`);
      if (!cell) continue;
      const pointNode = addNode({ id: `${plot.artifact_id}:point:${safeId(rowId, "row")}:${point.point_index ?? 0}`, type: "plot_point", label: `${plot.label} · ${rowId}`, cluster: "plot" });
      for (const sourceNodeId of cell.sourceNodeIds) {
        addEdge(sourceNodeId, pointNode.id, "consumed_by");
      }
      addEdge(pointNode.id, plotNode.id, "contains");
      pointCount += 1;
    }
  }
}

function rowIdFromScenario(row: TableRowRecord, rowIdField?: string | null): string {
  return String(
    (rowIdField ? row[rowIdField] : null) ??
      row.flat_view_row_id ??
      row.joined_row_id ??
      row.analysis_row_id ??
      row.row_id ??
      row._row_id ??
      row.id ??
      "row",
  );
}

export function applyProvenanceMetadata(
  rows: readonly ThreadRow[],
  assets: readonly ThreadAsset[],
  graph: V3ProvenanceGraph,
): { rows: ThreadRow[]; assets: ThreadAsset[] } {
  const rowsWithNodes = deriveRowsFromProvenanceGraph(graph, rows);
  const assetsWithNodes = assets.map((asset) => ({
    ...asset,
    provenanceNodeId: graph.assetNodeIdByAssetId.get(asset.id) ?? asset.provenanceNodeId,
    childNodeIds: graph.nodes
      .filter((node) => node.id.startsWith(`${graph.assetNodeIdByAssetId.get(asset.id) ?? asset.provenanceNodeId}:`))
      .map((node) => node.id),
  }));
  return { rows: rowsWithNodes, assets: assetsWithNodes };
}

export function healthForNode(value: string | null | undefined): ThreadHealth {
  if (value === "fresh" || value === "stale" || value === "warn" || value === "unknown") return value;
  return "unknown";
}
